import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useAttachDocumentsToElement,
  useElementAttachmentsRetrieval,
  useRetrieveDocuments,
  useUploadDocument,
} from "@planzone/view-models";
import { useMessage } from "@components/useMessage";
import { Identifiable, Document } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { Loader } from "@common/loader";
import { Preview } from "@common/preview";
import { DropZone } from "./dropZone";
import { DocumentCard } from "./documentCard";
import { Props } from "./types";
import styles from "./styles.scss";

function ElementAttachments({
  project,
  element,
  elementType,
  readOnly,
}: Props) {
  const [selectedDocument, setSelectedDocument] =
    React.useState<Identifiable<Document> | null>(null);

  const { t } = useTranslation();
  const {
    isRequestPending: isElementAttachmentsRetrievalPending,
    hasRetrievedElementAttachments,
    attachments,
    retrieveElementAttachments,
  } = useElementAttachmentsRetrieval(element.uuid);
  const { folder } = useRetrieveDocuments(project.uuid, "project");
  const { isRequestPending: isUploadPending, uploadDocument } =
    useUploadDocument(folder);
  const { isRequestPending: isAttachPending, attachDocumentsToElement } =
    useAttachDocumentsToElement(element.uuid);
  const { pushView } = useModal();
  const isUploadingRef = React.useRef(false);

  useMessage(
    (event) => {
      if (event.type === "uploadDocumentSucceeded" && isUploadingRef.current) {
        isUploadingRef.current = false;
        attachDocumentsToElement({
          projectId: project.uuid,
          elementType,
          elementId: element.uuid,
          documents: [event.data.document],
        });
      }
    },
    { localOnly: true },
  );

  React.useEffect(() => {
    if (
      project.uuid !== "" &&
      element.uuid !== "" &&
      !hasRetrievedElementAttachments
    ) {
      retrieveElementAttachments({
        elementId: element.uuid,
        elementType,
        projectId: project.uuid,
      });
    }
  }, [element.uuid, project.uuid, hasRetrievedElementAttachments]);

  const handleDrop = (file: {
    name: string;
    size: number;
    type: string;
    uri: string;
  }) => {
    if (!isUploadPending) {
      isUploadingRef.current = true;
      uploadDocument({
        namespace: project.uuid,
        type: "project",
        folder,
        file,
      });
    }
  };

  const handleAttach = () =>
    pushView({
      key: UseCases.attachDocumentsToElement,
      data: {
        project,
        elementId: element.uuid,
        elementType,
      },
    });

  const content = (
    <div className={styles.section}>
      <div className={styles.header}>
        <h3 className={styles.label}>
          {t("attachments.files")}
          {attachments.length > 0 && (
            <span className={styles.count}>{attachments.length}</span>
          )}
        </h3>
        {!readOnly && (
          <button className={styles.attachButton} onClick={handleAttach}>
            {t("attachments.add")}
          </button>
        )}
      </div>
      <div>
        {isElementAttachmentsRetrievalPending ? (
          <div className={styles.loaderContainer}>
            <Loader />
          </div>
        ) : attachments.length === 0 && !isUploadPending && !isAttachPending ? (
          !readOnly ? (
            <div className={styles.emptyDrop}>
              <p className={styles.emptyText}>
                {t("attachments.dropFileHere")}
              </p>
            </div>
          ) : (
            <p className={styles.emptyText}>{t("attachments.noFile")}</p>
          )
        ) : (
          <div className={styles.cards}>
            {attachments.map((doc) => (
              <DocumentCard
                element={element}
                elementType={elementType}
                key={doc.uuid}
                document={doc}
                openPreview={(document) => setSelectedDocument(document)}
              />
            ))}
            {(isUploadPending || isAttachPending) && (
              <div className={styles.uploadLoader}>
                <Loader />
              </div>
            )}
          </div>
        )}
      </div>
      {selectedDocument && (
        <Preview
          document={selectedDocument}
          close={() => setSelectedDocument(null)}
        />
      )}
    </div>
  );

  if (readOnly) {
    return content;
  }

  return (
    <DropZone className={styles.dropZone} drop={handleDrop}>
      {({ isDraggingOver }) => (
        <>
          {isDraggingOver && (
            <div className={styles.dropOverlay}>
              <p className={styles.dropOverlayText}>
                {t("documentUpload.message")}
              </p>
            </div>
          )}
          {content}
        </>
      )}
    </DropZone>
  );
}

export { ElementAttachments };
